// URL hash sync. Two-way: state ↔ window.location.hash.
//
// Format:
//   <hash> := raw DSL (URI-encoded)  |  'lz:' + LZ-string-encoded DSL
//
// Compression kicks in above LENGTH_THRESHOLD chars. Below that, hashes stay
// human-readable so anyone can edit a URL by hand.

import { effect } from './signals';
import { state, replaceState } from './state';
import { parseDsl, serializeDsl } from './dsl';
import { migrate } from './migrate';
import { compress, decompress } from '@/lib/compress';

const LZ_PREFIX = 'lz:';
const LENGTH_THRESHOLD = 200;
const DEBOUNCE_MS = 250;

export function startUrlSync(): () => void {
  // 1. Initial load — URL hash overrides whatever state was loaded from localStorage.
  if (typeof window !== 'undefined' && window.location.hash.length > 1) {
    loadFromHash();
  }

  // 2. Reactive: state changes → hash update (debounced).
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastWritten = '';

  const dispose = effect(() => {
    const s = state.get();
    if (typeof window === 'undefined') return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      const dsl = serializeDsl(s);
      const hash = encodeForHash(dsl);
      if (hash === lastWritten) return;
      const current = window.location.hash.slice(1);
      if (hash === current) {
        lastWritten = hash;
        return;
      }
      lastWritten = hash;
      try {
        history.replaceState(null, '', `${window.location.pathname}#${hash}`);
      } catch {
        // some browsers throw on excessive replaceState — silently ignore
      }
    }, DEBOUNCE_MS);
  });

  // 3. External hash changes (paste/back-button) load into state.
  function onHashChange(): void {
    loadFromHash();
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('hashchange', onHashChange);
  }

  return () => {
    dispose();
    if (timer !== null) clearTimeout(timer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('hashchange', onHashChange);
    }
  };
}

function loadFromHash(): boolean {
  const hash = window.location.hash.slice(1);
  if (!hash) return false;
  const dsl = decodeFromHash(hash);
  if (dsl === null) return false;
  const parsed = parseDsl(dsl);
  if (!parsed.ok) return false;
  const migrated = migrate(parsed.value);
  if (!migrated) return false;
  replaceState(
    {
      resolution: migrated.resolution,
      layers: migrated.layers,
      selectedLayerId: migrated.layers[0]?.id ?? null,
      soloLayerId: null,
      time: 0,
      paused: true,
      fps: 8,
    },
    false,
  );
  return true;
}

export function encodeForHash(dsl: string): string {
  if (dsl.length < LENGTH_THRESHOLD) {
    return encodeURIComponent(dsl);
  }
  return LZ_PREFIX + compress(dsl);
}

export function decodeFromHash(hash: string): string | null {
  if (hash.startsWith(LZ_PREFIX)) {
    return decompress(hash.slice(LZ_PREFIX.length));
  }
  try {
    return decodeURIComponent(hash);
  } catch {
    return null;
  }
}

export function shareableUrl(): string {
  if (typeof window === 'undefined') return '';
  const dsl = serializeDsl(state.peek());
  const hash = encodeForHash(dsl);
  return `${window.location.origin}${window.location.pathname}#${hash}`;
}

export function shareableDsl(): string {
  return serializeDsl(state.peek());
}
